const { getEnabledLocales } = require("./featureFlags");

const LOCALES = Object.freeze({
  EN_US: "en-us",
  ES_US: "es-us",
});

const DEFAULT_LOCALE = LOCALES.EN_US;

const SUPPORTED_LOCALES = {
  [LOCALES.EN_US]: {
    name: "English",
    isDefault: true,
  },
  [LOCALES.ES_US]: {
    name: "Español",
    isDefault: false,
  },
};

/**
 * Returns a localized path based on the provided locale
 * For the default locale (en-us), returns the original path (/)
 * For other locales, prepends the locale to the path
 *
 * @param {string} path - The original path
 * @param {string} locale - The locale code (e.g., "en-us", "es-us")
 * @returns {string} The localized path
 */
const getLocalizedPath = (path, locale) => {
  // Skip creating locale folder for default locale
  if (locale === DEFAULT_LOCALE) {
    return path;
  }

  return `/${locale}${path}`;
};

/**
 * Creates a clean path by removing leading and trailing slashes
 * and ensuring no double slashes
 *
 * @param {string} path - The path to clean
 * @returns {string} The cleaned path
 */
const cleanPath = (path) => {
  if (!path) return "";
  return `/${path.replace(/^\/|\/$/g, "").replace(/\/+/g, "/")}`;
};

/**
 * Gets metadata for a specific locale
 *
 * @param {string} locale - The locale code
 * @returns {Object|null} The locale metadata or null if not found
 */
const getLocaleMetadata = (locale) => {
  return SUPPORTED_LOCALES[locale] || null;
};

/**
 * Determines if a page should be created for a specific locale based on feature flags
 *
 * @param {string} locale - The locale code
 * @returns {boolean} Whether to create a page for this locale
 */
const shouldCreateLocale = (locale) => {
  return getEnabledLocales().includes(locale);
};

const getHrefLangUrl = ({ locale, host = process.env.CONTENTSTACK_HOSTED_URL, path }) => {
  const localePattern = new RegExp(`^/(${Object.values(LOCALES).join("|")})`);
  // remove locale from the path if it exists
  const cleanedPath = path.replace(localePattern, "");

  return `${host}${locale === DEFAULT_LOCALE ? "" : `/${locale}`}${cleanedPath}`;
};

const getLangCodeFromLocale = (locale) => {
  return locale?.split("-")[0];
};

module.exports = {
  LOCALES,
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  getLocalizedPath,
  cleanPath,
  getLocaleMetadata,
  shouldCreateLocale,
  getHrefLangUrl,
  getLangCodeFromLocale,
};
